/**
 * The Help Center's article format: a small, safe Markdown.
 *
 * One parser, four readers: the article page renders the tree as React
 * elements, the build-time pre-renderer writes it as HTML for search engines,
 * search indexes its plain text, and help-content.test.js checks its links
 * and wording. Nothing in an article is ever treated as raw HTML.
 *
 * Supported:
 *   ## Heading / ### Subheading     (the article title is the page's h1)
 *   paragraphs                      (blank-line separated)
 *   - bullet / 1. numbered          (consecutive lines; indent to continue)
 *   | table | rows |                (header row, then |---| separator)
 *   > [!TIP] / [!NOTE] / [!IMPORTANT] callouts (or a plain > quote)
 *   **bold**, [link](/help/section/article)
 *
 * Links may be site paths (/…), in-page anchors (#…), https://, mailto: or
 * tel:. Anything else renders as plain text.
 */

const CALLOUT_KINDS = { TIP: 'tip', NOTE: 'note', IMPORTANT: 'important', WARNING: 'important' };

export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80) || 'section';
}

export function isSafeHref(href) {
  const h = String(href || '').trim();
  return /^(\/(?!\/)|#|https:\/\/|mailto:|tel:)/i.test(h);
}

/** Inline text → nodes: text, strong, link. */
export function parseInline(text) {
  const nodes = [];
  const src = String(text || '');
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)\s]+)\)/g;
  let last = 0;
  let m;
  while ((m = pattern.exec(src))) {
    if (m.index > last) nodes.push({ type: 'text', value: src.slice(last, m.index) });
    if (m[1] !== undefined) {
      nodes.push({ type: 'strong', children: [{ type: 'text', value: m[1] }] });
    } else if (isSafeHref(m[3])) {
      nodes.push({ type: 'link', href: m[3], children: parseInline(m[2]) });
    } else {
      nodes.push({ type: 'text', value: m[2] });
    }
    last = pattern.lastIndex;
  }
  if (last < src.length) nodes.push({ type: 'text', value: src.slice(last) });
  return nodes;
}

const isHeading = (l) => /^#{2,3}\s+\S/.test(l);
const isQuote = (l) => /^>\s?/.test(l);
const isBullet = (l) => /^[-*]\s+\S/.test(l);
const isNumbered = (l) => /^\d+\.\s+\S/.test(l);
const isTableRow = (l) => /^\|.*\|\s*$/.test(l);
const isTableSeparator = (l) => /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)*\|\s*$/.test(l);
const isContinuation = (l) => /^\s{2,}\S/.test(l);

function splitRow(line) {
  return line.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
}

/**
 * Parse an article body. Returns `{ blocks, headings }`, where headings are
 * the h2/h3 entries for the "On this page" sidebar.
 */
export function parseMarkdown(source) {
  const lines = String(source || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  const headings = [];
  const usedIds = new Map();
  const uniqueId = (text) => {
    const base = slugify(text);
    const n = (usedIds.get(base) || 0) + 1;
    usedIds.set(base, n);
    return n === 1 ? base : `${base}-${n}`;
  };

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (!line.trim()) { i += 1; continue; }

    if (isHeading(line)) {
      const level = line.startsWith('###') ? 3 : 2;
      const text = line.replace(/^#{2,3}\s+/, '').trim();
      const id = uniqueId(text);
      blocks.push({ type: 'heading', level, id, children: parseInline(text) });
      headings.push({ id, level, text: toPlainText([{ type: 'paragraph', children: parseInline(text) }]) });
      i += 1;
      continue;
    }

    if (isQuote(line)) {
      const quoted = [];
      while (i < lines.length && isQuote(lines[i])) {
        quoted.push(lines[i].replace(/^>\s?/, ''));
        i += 1;
      }
      let kind = 'quote';
      const marker = quoted[0].match(/^\[!([A-Z]+)\]\s*/);
      if (marker && CALLOUT_KINDS[marker[1]]) {
        kind = CALLOUT_KINDS[marker[1]];
        quoted[0] = quoted[0].slice(marker[0].length);
      }
      const paragraphs = quoted.join('\n').split(/\n\s*\n/)
        .map((p) => p.split('\n').map((s) => s.trim()).filter(Boolean).join(' '))
        .filter(Boolean)
        .map((p) => ({ type: 'paragraph', children: parseInline(p) }));
      blocks.push({ type: 'callout', kind, children: paragraphs });
      continue;
    }

    if (isBullet(line) || isNumbered(line)) {
      const ordered = isNumbered(line);
      const items = [];
      while (i < lines.length && (ordered ? isNumbered(lines[i]) : isBullet(lines[i]))) {
        let text = lines[i].replace(ordered ? /^\d+\.\s+/ : /^[-*]\s+/, '');
        i += 1;
        while (i < lines.length && isContinuation(lines[i])) {
          text += ` ${lines[i].trim()}`;
          i += 1;
        }
        items.push(parseInline(text.trim()));
      }
      blocks.push({ type: 'list', ordered, items });
      continue;
    }

    if (isTableRow(line) && i + 1 < lines.length && isTableSeparator(lines[i + 1])) {
      const header = splitRow(line).map(parseInline);
      i += 2;
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitRow(lines[i]).map(parseInline));
        i += 1;
      }
      blocks.push({ type: 'table', header, rows });
      continue;
    }

    const para = [];
    while (
      i < lines.length && lines[i].trim()
      && !isHeading(lines[i]) && !isQuote(lines[i]) && !isBullet(lines[i]) && !isNumbered(lines[i])
      && !(isTableRow(lines[i]) && i + 1 < lines.length && isTableSeparator(lines[i + 1]))
    ) {
      para.push(lines[i].trim());
      i += 1;
    }
    blocks.push({ type: 'paragraph', children: parseInline(para.join(' ')) });
  }
  return { blocks, headings };
}

function inlineText(nodes) {
  return (nodes || []).map((n) => (n.type === 'text' ? n.value : inlineText(n.children))).join('');
}

/** Plain text of a block list (search, summaries, checks). */
export function toPlainText(blocks) {
  const out = [];
  for (const b of blocks || []) {
    if (b.type === 'heading' || b.type === 'paragraph') out.push(inlineText(b.children));
    else if (b.type === 'callout') out.push(toPlainText(b.children));
    else if (b.type === 'list') out.push(b.items.map(inlineText).join('\n'));
    else if (b.type === 'table') out.push([b.header, ...b.rows].map((r) => r.map(inlineText).join(' | ')).join('\n'));
  }
  return out.join('\n\n');
}

/** Every link target in a block list. */
export function collectLinks(blocks) {
  const hrefs = [];
  const walkInline = (nodes) => {
    for (const n of nodes || []) {
      if (n.type === 'link') hrefs.push(n.href);
      if (n.children) walkInline(n.children);
    }
  };
  for (const b of blocks || []) {
    if (b.type === 'heading' || b.type === 'paragraph') walkInline(b.children);
    else if (b.type === 'callout') hrefs.push(...collectLinks(b.children));
    else if (b.type === 'list') b.items.forEach(walkInline);
    else if (b.type === 'table') [b.header, ...b.rows].forEach((r) => r.forEach(walkInline));
  }
  return hrefs;
}

const escapeHtml = (s) => String(s)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function inlineHtml(nodes) {
  return (nodes || []).map((n) => {
    if (n.type === 'text') return escapeHtml(n.value);
    if (n.type === 'strong') return `<strong>${inlineHtml(n.children)}</strong>`;
    if (n.type === 'link') {
      const external = /^https:\/\//i.test(n.href);
      return `<a href="${escapeHtml(n.href)}"${external ? ' rel="noopener noreferrer" target="_blank"' : ''}>${inlineHtml(n.children)}</a>`;
    }
    return '';
  }).join('');
}

/** Static HTML for the pre-rendered page search engines read. */
export function toHtml(blocks) {
  return (blocks || []).map((b) => {
    if (b.type === 'heading') return `<h${b.level} id="${escapeHtml(b.id)}">${inlineHtml(b.children)}</h${b.level}>`;
    if (b.type === 'paragraph') return `<p>${inlineHtml(b.children)}</p>`;
    if (b.type === 'callout') return `<aside data-callout="${b.kind}">${toHtml(b.children)}</aside>`;
    if (b.type === 'list') {
      const tag = b.ordered ? 'ol' : 'ul';
      return `<${tag}>${b.items.map((it) => `<li>${inlineHtml(it)}</li>`).join('')}</${tag}>`;
    }
    if (b.type === 'table') {
      const head = `<tr>${b.header.map((c) => `<th>${inlineHtml(c)}</th>`).join('')}</tr>`;
      const rows = b.rows.map((r) => `<tr>${r.map((c) => `<td>${inlineHtml(c)}</td>`).join('')}</tr>`).join('');
      return `<table><thead>${head}</thead><tbody>${rows}</tbody></table>`;
    }
    return '';
  }).join('\n');
}
